// Client-side video compression using the hardware-accelerated video decode
// path (HTMLVideoElement) + HTMLCanvasElement downscale + MediaRecorder
// (hardware-accelerated encode where available). Produces a WebM file with
// VP9+Opus when supported, falling back to VP8.
//
// This is realtime: compressing a 2-minute clip takes ~2 minutes. Progress
// is emitted continuously via onProgress (0..1) so the UI can show a bar.
// Pass an AbortSignal to cancel mid-stream.

export interface CompressVideoOptions {
  maxHeight?: number             // target height cap (default 480)
  videoBitsPerSecond?: number    // encoder target bitrate (default 1.2 Mbps)
  onProgress?: (frac: number) => void
  signal?: AbortSignal
}

// HTMLVideoElement now declares requestVideoFrameCallback /
// cancelVideoFrameCallback in lib.dom. captureStream isn't in the standard
// lib yet, and Firefox still ships mozCaptureStream, so we augment only
// those via an intersection type below.
type CaptureCapableVideo = HTMLVideoElement & {
  captureStream?: () => MediaStream
  mozCaptureStream?: () => MediaStream
}

function pickMimeType(): string | null {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ]
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m
  }
  return null
}

export function isVideoCompressionSupported(): boolean {
  return typeof MediaRecorder !== 'undefined' && pickMimeType() !== null
}

export async function compressVideo(
  file: File,
  opts: CompressVideoOptions = {}
): Promise<File> {
  const maxHeight = opts.maxHeight ?? 480
  const videoBitrate = opts.videoBitsPerSecond ?? 1_200_000
  const onProgress = opts.onProgress
  const signal = opts.signal

  const mimeType = pickMimeType()
  if (!mimeType) throw new Error('Browser does not support WebM recording')

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  const url = URL.createObjectURL(file)
  const video = document.createElement('video') as CaptureCapableVideo
  video.src = url
  video.playsInline = true
  video.muted = true
  video.preload = 'auto'
  // Source is same-origin via object URL — no crossOrigin needed.

  const cleanup: Array<() => void> = []
  cleanup.push(() => URL.revokeObjectURL(url))

  try {
    await new Promise<void>((resolve, reject) => {
      const onMeta = () => resolve()
      const onErr = () => reject(new Error('Failed to load video metadata'))
      video.addEventListener('loadedmetadata', onMeta, { once: true })
      video.addEventListener('error', onErr, { once: true })
    })

    const srcW = video.videoWidth
    const srcH = video.videoHeight
    if (!srcW || !srcH) throw new Error('Invalid video dimensions')

    const scale = Math.min(1, maxHeight / srcH)
    const outW = Math.max(2, Math.round((srcW * scale) / 2) * 2)
    const outH = Math.max(2, Math.round((srcH * scale) / 2) * 2)

    const canvas = document.createElement('canvas')
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('Canvas 2D context unavailable')

    // Draw-loop: use requestVideoFrameCallback when available (fires per
    // decoded frame, so the canvas stream only advances when there is new
    // content). Fall back to rAF on browsers without it.
    const useRVFC = typeof video.requestVideoFrameCallback === 'function'
    let rafHandle = 0
    let rvfcHandle = 0
    let stopped = false
    const drawOnce = () => {
      try {
        ctx.drawImage(video, 0, 0, outW, outH)
      } catch { /* draw may fail briefly around seeks; ignore */ }
    }
    const tickRVFC = () => {
      if (stopped) return
      drawOnce()
      rvfcHandle = video.requestVideoFrameCallback!(tickRVFC)
    }
    const tickRAF = () => {
      if (stopped) return
      drawOnce()
      rafHandle = requestAnimationFrame(tickRAF)
    }
    cleanup.push(() => {
      stopped = true
      if (useRVFC && video.cancelVideoFrameCallback && rvfcHandle) {
        video.cancelVideoFrameCallback(rvfcHandle)
      }
      if (!useRVFC && rafHandle) cancelAnimationFrame(rafHandle)
    })

    // Canvas video track, plus the source's audio track (if any).
    const canvasStream = canvas.captureStream(30)
    const srcStream = video.captureStream?.() ?? video.mozCaptureStream?.()
    const audioTracks = srcStream?.getAudioTracks() ?? []
    const combined = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioTracks,
    ])

    const recorder = new MediaRecorder(combined, {
      mimeType,
      videoBitsPerSecond: videoBitrate,
    })
    const chunks: Blob[] = []
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunks.push(e.data)
    }

    const done = new Promise<void>((resolve, reject) => {
      recorder.onstop = () => resolve()
      recorder.onerror = (e: Event) => {
        const err = (e as unknown as { error?: Error }).error
        reject(err instanceof Error ? err : new Error('Recorder error'))
      }
    })

    let aborted = false
    const onAbort = () => {
      aborted = true
      try { video.pause() } catch { /* noop */ }
      if (recorder.state !== 'inactive') { try { recorder.stop() } catch { /* noop */ } }
    }
    if (signal) {
      signal.addEventListener('abort', onAbort)
      cleanup.push(() => signal.removeEventListener('abort', onAbort))
    }

    const onTime = () => {
      if (onProgress && video.duration > 0 && isFinite(video.duration)) {
        onProgress(Math.min(1, video.currentTime / video.duration))
      }
    }
    video.addEventListener('timeupdate', onTime)
    cleanup.push(() => video.removeEventListener('timeupdate', onTime))

    const onEnded = () => {
      // Flush final frame before stopping the encoder so the last moment
      // of video is captured.
      drawOnce()
      if (recorder.state !== 'inactive') { try { recorder.stop() } catch { /* noop */ } }
    }
    video.addEventListener('ended', onEnded)
    cleanup.push(() => video.removeEventListener('ended', onEnded))

    // Start the draw loop and recorder, then begin playback.
    if (useRVFC) tickRVFC(); else tickRAF()
    recorder.start(1000)
    await video.play()
    await done

    if (aborted) throw new DOMException('Aborted', 'AbortError')

    const blob = new Blob(chunks, { type: 'video/webm' })
    const baseName = file.name.replace(/\.[^.]+$/, '')
    const outFile = new File([blob], `${baseName}.webm`, {
      type: 'video/webm',
      lastModified: Date.now(),
    })
    onProgress?.(1)
    return outFile
  } finally {
    // Run cleanup in reverse order so later-added listeners are removed first.
    for (let i = cleanup.length - 1; i >= 0; i--) {
      try { cleanup[i]!() } catch { /* noop */ }
    }
    video.src = ''
  }
}
